import { Inject, Injectable } from '@angular/core';

import { OfferSource } from '@cds';
import {
    Fixture,
    FixtureStage,
    Option,
    OptionMarket,
    OptionMarketStatus,
    OptionStatus,
    Participant,
    ParticipantPriceType,
    ParticipantType,
    TotalsPrefix,
} from '@cds/betting-offer';
import { EventModel, Tv2MarketParameters } from '@frontend/sports/betting-offer/feature/model';
import { MarketSubType, MarketType } from '@frontend/sports/betting-offer/feature/types';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { hashCode } from '@frontend/sports/common/core/utils/string';
import { StringInterpolationPipe } from '@frontend/sports/common/core/utils/string-interpolation';
import { CustomerOfferService } from '@frontend/sports/new-customer-offer-feature';
import { Odds, OddsOperations } from '@frontend/sports/odds/feature';
import { NativePrice } from '@frontend/sports/odds/feature/native-price';
import { RacingOptionMarketTypes } from '@frontend/sports/racing/feature/models';
import { BetBuilderMarket } from 'packages/sports/web/app/src/bet-builder/model/bet-builder.model';
import { HorsesFixtureHelper } from 'packages/sports/web/app/src/horse-racing-shared/services/horses-fixture-helper.service';
import { HorsesFixtureMarketHelper } from 'packages/sports/web/app/src/horse-racing-shared/services/horses-fixture-market-helper.service';
import { EachWayMarketHelperService } from 'packages/sports/web/app/src/option-group/each-way-market-helper.service';

import { BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN, IBetslipMessagesProviderService } from '../../../base/services/betslip-messages-provider.service';
import { formatObject } from '../../../base/utils/format-object';
import { getLeagueId } from '../../../core/betslip-leagueid-utils';
import { BetBuilderV1EventData } from '../../../core/external-betslip-actions';
import { BetslipBetBuilderPick, SportcastOption } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { BetslipV2StandardPick } from '../../../core/picks/betslip-v2-standard-pick';
import { CombinationPrevention, toComboPrevention } from '../../../core/picks/combo-prevention';
import { MinimumCombination, toMinimumComboTV2 } from '../../../core/picks/minimum-combination';
import { BetBuilderPickId, V2OptionMarketPickId, V2OptionMarketXCastPickId } from '../../../core/picks/pick-id';
import { IBetslipV2OptionMarket, IPrice, ParticipantPickType, PriceType } from '../../../core/picks/pick-models';
import { SignedName, createSignedName } from '../../../core/picks/signed-name.model';
import {
    BetslipV2HorseRaceOptionMarketPick,
    BetslipV2OptionMarketXCastRacePick,
} from '../../../core/picks/sport-specific/betslip-v2-horse-race-picks';
import { optionPriceToBetslipPrice } from '../../../core/price';
import AlternativeTranslationsService from '../../../integration/services/alternative-translations-impl.service';
import V2PickFactory from './v2-pick.factory';

type OptionMarketsFixture = Omit<Fixture, 'games'>;

@Injectable()
export default class OptionPickFactory extends V2PickFactory {
    private messages: {
        racingBetTypes: {
            betTypeWin: string;
            betTypeWinAlt: string;
            betTypeForecast: string;
            betTypeForecastAlt: string;
            betTypeTricast: string;
            betTypeTricastAlt: string;
            betTypeCombinationForecast: string;
            betTypeCombinationForecastAlt: string;
            betTypeCombinationTricast: string;
            betTypeCombinationTricastAlt: string;
        };
        racingMarketNames: {
            combinationForecast: string;
            combinationForecastAlt: string;
            combinationTricast: string;
            combinationTricastAlt: string;
            forecast: string;
            forecastAlt: string;
            tricast: string;
            tricastAlt: string;
            win: string;
            winAlt: string;
            bettingWithout: string;
            freeFormed: string;
            insurance2Places: string;
            insurance3Places: string;
            insurance4Places: string;
            toFinish2: string;
            toFinish3: string;
            toFinish4: string;
            topFinish2: string;
            topFinish3: string;
            topFinish4: string;
            winBy3Lengths: string;
            winBy1Lengths: string;
            winEw: string;
            winEwExtraPlace: string;
            winnerByHowFar: string;
            optionNameWinnerByHowFar: string;
            specials: string;
        };
    };

    constructor(
        private customerOfferService: CustomerOfferService,
        private betslipConfig: BetslipConfig,
        @Inject(BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messageProvider: IBetslipMessagesProviderService,
        private alternativeTranslation: AlternativeTranslationsService,
        private easyBetMarketHelperService: EachWayMarketHelperService,
        private stringInterpolationPipe: StringInterpolationPipe,
        private horsesFixtureHelper: HorsesFixtureHelper,
        private horsesFixtureMarketHelper: HorsesFixtureMarketHelper,
    ) {
        super();
        const { racingBetTypes, racingMarketNames } = messageProvider.getMessages();
        this.messages = { racingBetTypes, racingMarketNames };
    }

    createOptionMarketPick(pickId: V2OptionMarketPickId, fixture: OptionMarketsFixture, isPriceBoosted?: boolean): BetslipV2OptionMarketPick | null {
        let pick: BetslipV2OptionMarketPick;
        switch (fixture.sport.id) {
            case SportConstant.Horses:
            case SportConstant.Greyhounds: {
                fixture.addons.bestOddsGuarantee = this.horsesFixtureHelper.checkIfBOGApplicable(fixture);

                pick = new BetslipV2HorseRaceOptionMarketPick();
                pick.fixture = this.mapHorseFixture(fixture);
                break;
            }
            default: {
                pick = new BetslipV2StandardPick();
                pick.fixture = this.mapFixture(fixture);
                pick.isFromNewCustomerOffer = this.customerOfferService.isNewCustomerOffer(fixture.competition.id);
            }
        }

        pick.id = pickId;

        return this.mapOptionMarketPick(pick, fixture, isPriceBoosted) ? pick : null;
    }

    createOptionMarketXCastPick(pickId: V2OptionMarketXCastPickId, fixture: OptionMarketsFixture): BetslipV2OptionMarketXCastRacePick | null {
        const pick = new BetslipV2OptionMarketXCastRacePick();
        pick.id = pickId;
        pick.fixture = this.mapHorseFixture(fixture);
        const optionMarket = fixture.optionMarkets.find((m) => m.id === pick.id.optionMarketId);
        if (!optionMarket) {
            return pick;
        }
        let options: Option[] = [];
        options = optionMarket.options.filter((o) => pick.id.optionIds[o.id]);

        if (!options) {
            return pick;
        }

        pick.options = options.map((o) => {
            return {
                id: o.id,
                name: createSignedName(o.name),
                isVisible: o.status === OptionStatus.Visible,
                prices: [optionPriceToBetslipPrice(optionMarket, o.price)],
                position: pick.id.pickType === ParticipantPickType.Win || !pick.id.optionIds ? 1 : pick.id.optionIds[o.id],
            };
        });
        optionMarket.name.value = this.setHorseRaceParticipantPickMarketName(pick);

        pick.market = {
            id: optionMarket.id,
            isVisible: optionMarket.status === OptionMarketStatus.Visible,
            isClosed: optionMarket.status === OptionMarketStatus.Hidden,
            name: createSignedName(optionMarket.name),
            comboPrevention: toComboPrevention(optionMarket.comboPrevention),
            minimumCombo: toMinimumComboTV2(optionMarket.minCombo),
            gridGroups: optionMarket.grouping?.gridGroups,
            marketType: optionMarket.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value as MarketType,
            parameters: optionMarket.parameters,
            isEachWay: optionMarket.isEachWay!,
            placeTerms: optionMarket.placeTerms,
            isStartingPriceAvailable: true,
            isFixedPriceAvailable: !!optionMarket.isWinPriceEnabled,
            isBetBuilderEnabled: optionMarket.isBetBuilder ?? false,
        };

        pick.priceType = this.getWinPickPriceType(pick.market, pick.prices);
        this.setParticipantPickBetType(pick);
        this.setParticipantPickBetTypeAltTranslation(pick);

        pick.acceptedPrice = {
            price: pick.currentPrice ? { ...pick.currentPrice } : null,
            isManuallyAccepted: false,
        };

        return pick;
    }

    createBetBuilderPick(pickId: BetBuilderPickId, fixture: OptionMarketsFixture, event: BetBuilderV1EventData | null): BetslipBetBuilderPick | null {
        const pick = new BetslipBetBuilderPick();
        pick.id = pickId;
        pick.fixture = this.mapFixture(fixture);
        this.mapOptionMarketPick(pick, fixture);
        pick.market.comboPrevention = this.betslipConfig.allowBetbuilderCombos ? CombinationPrevention.NoFixtureCombo : CombinationPrevention.NoCombo;
        pick.eventId = event?.event.id || fixture.id;

        if (event) {
            pick.event = {
                league: {
                    id: getLeagueId(event),
                    name: {
                        name: event.league.name,
                        signature: event.league.name,
                        ...(event.league.name && { nameAlternate: event.league.name, signatureAlternate: event.league.name }),
                    },
                    parentLeagueId: event.league.parentLeagueId,
                },
                event: {
                    id: event.event.id,
                    name: {
                        name: event.event.name,
                        signature: event.event.name,
                        ...(event.event.name && { nameAlternate: event.event.name, signatureAlternate: event.event.name }),
                    },
                    date: event.event.date,
                    isLive: event.event.isLive,
                    groupId: event.event.groupId,
                },
                region: {
                    id: event.region.id,
                    name: {
                        name: event.region.name,
                        signature: event.region.name,
                        ...(event.region.name && { nameAlternate: event.region.name, signatureAlternate: event.region.name }),
                    },
                },
                source: event.event.id.includes(':') ? OfferSource.V2 : OfferSource.V1,
                betBuilderTradingV2FixtureId: event.betBuilderTradingV2FixtureId,
            };
        } else {
            // This is just to set some dummy values.
            // Systems like edit my bet doesn't support bet builder picks so we don't need to fill event with real values.
            pick.event = {
                league: {
                    id: fixture.competition?.id || 0,
                    name: createSignedName(fixture.competition?.name || ''),
                },
                region: {
                    id: fixture.region?.id || 0,
                    name: createSignedName(fixture.region?.name || ''),
                },
                event: {
                    id: fixture.id,
                    name: createSignedName(fixture.name),
                    date: new Date(fixture.startDate),
                },
                source: OfferSource.BetBuilder,
            };
        }

        return pick;
    }

    createEntainUiBetBuilderPick(
        pickId: BetBuilderPickId,
        event: BetBuilderV1EventData,
        eventModel: EventModel,
        optionMarket: BetBuilderMarket,
        sportcastOptions: SportcastOption[],
    ): BetslipBetBuilderPick {
        const pick = new BetslipBetBuilderPick();
        pick.id = pickId;
        pick.sportcastOptions = sportcastOptions;

        pick.betgeniusId = Number(eventModel.getBetBuilderConsumerFixtureId());
        pick.sportcastId = eventModel.sportcastId;
        pick.useV2Key = !!eventModel.betBuilderTradingV2FixtureId;

        pick.fixture = {
            competitionGroupId: eventModel.competitionGroupId || null,
            cutOffDate: new Date(eventModel.cutOffDate),
            eventDate: new Date(eventModel.startDate),
            fixtureId: pickId.fixtureId,
            fixtureType: eventModel.fixtureType,
            league: eventModel.league
                ? {
                      id: eventModel.league.id,
                      name: createSignedName(eventModel.league.name),
                  }
                : null,
            name: createSignedName(eventModel.name),
            virtual: false,
            region: eventModel.region
                ? {
                      id: eventModel.region.id,
                      name: createSignedName(eventModel.region.name),
                  }
                : null,
            sportId: eventModel.sport.id,
            sportName: createSignedName(eventModel.sport.name),
            stage: FixtureStage.PreMatch,
            linkedEventIds: [],
        };

        const nativeOdds = !optionMarket.option.optionPrice.nativePrice.isEmpty()
            ? optionMarket.option.optionPrice.nativePrice.toJSON()
            : NativePrice.empty().toJSON();

        this.mapEntainUIBABMarketOptionAcceptedPrice(
            pick,
            optionMarket.id,
            optionMarket.option.id,
            createSignedName(optionMarket.name),
            createSignedName(optionMarket.name),
            OddsOperations.createOdds(nativeOdds),
        );

        pick.eventId = pickId.eventId;
        pick.event = {
            league: {
                id: getLeagueId(event),
                name: {
                    name: event.league.name,
                    signature: event.league.name,
                    ...(event.league.name && { nameAlternate: event.league.name, signatureAlternate: event.league.name }),
                },
                parentLeagueId: event.league.parentLeagueId,
            },
            event: {
                id: event.event.id,
                name: {
                    name: event.event.name,
                    signature: event.event.name,
                    ...(event.event.name && { nameAlternate: event.event.name, signatureAlternate: event.event.name }),
                },
                date: event.event.date,
                isLive: event.event.isLive,
                groupId: event.event.groupId,
                participants: eventModel.participants,
                viewType: eventModel.viewType,
            },
            region: {
                id: event.region.id,
                name: {
                    name: event.region.name,
                    signature: event.region.name,
                    ...(event.region.name && { nameAlternate: event.region.name, signatureAlternate: event.region.name }),
                },
            },
            source: event.event.id.includes(':') ? OfferSource.V2 : OfferSource.V1,
            betBuilderTradingV2FixtureId: event.betBuilderTradingV2FixtureId,
        };

        return pick;
    }

    convertPreCreatedBABPick(pick: BetslipBetBuilderPick): BetslipBetBuilderPick {
        const longIds = pick.longIds!;
        const sportCastOptions = pick.marketName.name.split(';').reduce((options: SportcastOption[], name: string, index) => {
            options.push({ longId: longIds[index], name });

            return options;
        }, []);

        const optionId = Math.abs(hashCode(sportCastOptions[0].longId) + pick.sportcastId!);

        const pickId = new BetBuilderPickId(pick.eventId, pick.fixture.fixtureId, optionId, optionId, optionId);
        const newPick = pick.copy();
        newPick.id = pickId;
        newPick.sportcastOptions = sportCastOptions;

        this.mapEntainUIBABMarketOptionAcceptedPrice(
            newPick,
            optionId,
            optionId,
            pick.marketName,
            pick.optionName,
            pick.acceptedPrice.price!.nativeOdds,
        );

        return newPick;
    }

    mapOptionMarketPick(pick: BetslipV2OptionMarketPick, fixture: OptionMarketsFixture, isPriceBoosted?: boolean): boolean {
        const optionMarket = fixture.optionMarkets.find((m) => m.options.some((o) => o.id === pick.id.optionId));
        if (!optionMarket) {
            return false;
        }

        const option = optionMarket.options.find((o) => o.id === pick.id.optionId)!;

        if (!option) {
            return false;
        }

        let participant;

        if (pick instanceof BetslipV2HorseRaceOptionMarketPick) {
            participant = fixture.participants?.find((p) => option.parameters?.fixtureParticipant === p.id);

            if (optionMarket.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === RacingOptionMarketTypes.WinnerByHowFar) {
                const winnerMarket = this.messages.racingMarketNames.optionNameWinnerByHowFar;
                if (winnerMarket) {
                    option.name.value = this.stringInterpolationPipe.transform(this.messages.racingMarketNames.optionNameWinnerByHowFar, {
                        participantName: fixture.participants?.find((p) => optionMarket.fixtureParticipantId === p.id)?.name.value,
                        optionName: option.name.value,
                    });
                }
            }
            this.getMarketName(optionMarket, fixture);
        }
        this.mapFromOptions(pick, optionMarket, option, isPriceBoosted, participant);

        return true;
    }

    mapFromOptions(
        pick: BetslipV2OptionMarketPick,
        optionMarket: OptionMarket,
        option: Option,
        isPriceBoosted?: boolean,
        participant?: Participant,
    ): void {
        pick.market = {
            id: optionMarket.id,
            isVisible: optionMarket.status === OptionMarketStatus.Visible,
            isClosed: optionMarket.status === OptionMarketStatus.Hidden,
            name: optionMarket.compositeName ? createSignedName(optionMarket.compositeName, true) : createSignedName(optionMarket.name),
            comboPrevention: toComboPrevention(optionMarket.comboPrevention),
            minimumCombo: toMinimumComboTV2(optionMarket.minCombo),
            gridGroups: optionMarket.grouping?.gridGroups,
            marketType: optionMarket.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value as MarketType,
            marketSubType: optionMarket.parameters?.find((p) => p.key === Tv2MarketParameters.MarketSubType)?.value as MarketSubType,
            parameters: optionMarket.parameters,
            isEachWay: optionMarket.isEachWay! && participant?.properties?.type !== ParticipantType.Favourite,
            placeTerms: optionMarket.placeTerms,
            isStartingPriceAvailable: !!optionMarket.isStartingPriceEnabled,
            isFixedPriceAvailable:
                !!optionMarket.isWinPriceEnabled &&
                participant?.properties?.type !== ParticipantType.Favourite &&
                optionMarket.options.find(
                    (o) =>
                        o.participantPriceType === ParticipantPriceType.Fixed &&
                        option.parameters?.fixtureParticipant === o.parameters?.fixtureParticipant,
                )?.price.odds! > 1,
            isBetBuilderEnabled: optionMarket.isBetBuilder ?? false,
            isTeasable: optionMarket.isTeasable,
        };

        pick.option = {
            id: option.id,
            name: createSignedName(option.name),
            isVisible: option.status === OptionStatus.Visible,
            prices: [optionPriceToBetslipPrice(optionMarket, option.price)],
            isDraw: option.parameters?.optionTypes?.some((p) => p === 'Draw'),
        };

        pick.isFavouritePick = participant?.properties?.type === ParticipantType.Favourite;
        if (isPriceBoosted && option.boostedPrice) {
            pick.boostedPriceDetails = {
                boostedPrice: optionPriceToBetslipPrice(optionMarket, option.boostedPrice),
                boostedPriceId: option.boostedPrice.id,
                isPriceBoosted,
            };
        }

        pick.acceptedPrice = {
            price: pick.currentPrice ? { ...pick.currentPrice } : null,
            isManuallyAccepted: false,
        };

        pick.totalsPrefix =
            pick.market.marketType == MarketType.OverAndUnder
                ? option.parameters.optionTypes[0] == TotalsPrefix.Over
                    ? TotalsPrefix.Over
                    : TotalsPrefix.Under
                : undefined;

        if (pick instanceof BetslipV2HorseRaceOptionMarketPick) {
            const siblingOption = optionMarket.options?.find((o) => o.parameters.fixtureParticipant === participant?.id && o.id !== option.id);
            if (siblingOption) {
                pick.siblingOption = {
                    id: siblingOption.id,
                    name: createSignedName(siblingOption.name),
                    isVisible: siblingOption.status === OptionStatus.Visible,
                    prices: [optionPriceToBetslipPrice(optionMarket, siblingOption.price)],
                };
            }

            pick.pickType =
                pick.market.marketType?.toString() === 'WinEW' || pick.market.marketType?.toString() === 'WinEWExtraPlace'
                    ? ParticipantPickType.Win
                    : ParticipantPickType.Derived;

            pick.priceType = this.getWinPickPriceType(pick.market, pick.prices);
            this.setParticipantPickBetType(pick);
            this.setParticipantPickBetTypeAltTranslation(pick);
        }
    }

    getWinPickPriceType(market: IBetslipV2OptionMarket, prices: IPrice[]): PriceType {
        return market.isVisible && prices.some((pr) => pr.isVisible && pr.type === PriceType.Fixed) ? PriceType.Fixed : PriceType.StartingPrice;
    }

    private setParticipantPickBetType(pick: BetslipV2HorseRaceOptionMarketPick | BetslipV2OptionMarketXCastRacePick): void {
        const pickType = pick instanceof BetslipV2HorseRaceOptionMarketPick ? pick.pickType : pick.id.pickType;
        switch (pickType) {
            case ParticipantPickType.CombinationForecast:
                pick.betType = formatObject(this.messages.racingBetTypes.betTypeCombinationForecast, [pick.betCount()]);
                break;
            case ParticipantPickType.CombinationTricast:
                pick.betType = formatObject(this.messages.racingBetTypes.betTypeCombinationTricast, [pick.betCount()]);
                break;
            case ParticipantPickType.Forecast:
                pick.betType = this.messages.racingBetTypes.betTypeForecast;
                break;
            case ParticipantPickType.Tricast:
                pick.betType = this.messages.racingBetTypes.betTypeTricast;
                break;
            case ParticipantPickType.Win:
            case ParticipantPickType.Derived:
                pick.betType = this.messages.racingBetTypes.betTypeWin;
                break;
        }
    }

    /**
     * Set alt bet type. In betstation for the receipt only english should be used,
     * Alt messages are only in English while its sibling is in user language.
     *
     * @param pick
     * @private
     */
    private setParticipantPickBetTypeAltTranslation(pick: BetslipV2HorseRaceOptionMarketPick | BetslipV2OptionMarketXCastRacePick): void {
        if (!this.alternativeTranslation.useBaseTranslations() || !this.messages.racingBetTypes.betTypeWinAlt) {
            return;
        }
        const pickType = pick instanceof BetslipV2HorseRaceOptionMarketPick ? pick.pickType : pick.id.pickType;

        switch (pickType) {
            case ParticipantPickType.CombinationForecast:
                pick.betTypeAltTranslation = formatObject(this.messages.racingBetTypes.betTypeCombinationForecastAlt, [pick.betCount()]);
                break;
            case ParticipantPickType.CombinationTricast:
                pick.betTypeAltTranslation = formatObject(this.messages.racingBetTypes.betTypeCombinationTricastAlt, [pick.betCount()]);
                break;
            case ParticipantPickType.Forecast:
                pick.betTypeAltTranslation = this.messages.racingBetTypes.betTypeForecastAlt;
                break;
            case ParticipantPickType.Tricast:
                pick.betTypeAltTranslation = this.messages.racingBetTypes.betTypeTricastAlt;
                break;
            case ParticipantPickType.Win:
            case ParticipantPickType.Derived:
                pick.betTypeAltTranslation = this.messages.racingBetTypes.betTypeWinAlt;
                break;
            default:
                pick.betTypeAltTranslation = this.messages.racingBetTypes.betTypeWinAlt;
                break;
        }
    }

    private setHorseRaceParticipantPickMarketName(pick: BetslipV2OptionMarketXCastRacePick): string {
        switch (pick.id.pickType) {
            case ParticipantPickType.CombinationForecast:
                return `${pick.betCount()}x ${this.messages.racingMarketNames.combinationForecast}`;
            case ParticipantPickType.CombinationTricast:
                return `${pick.betCount()}x ${this.messages.racingMarketNames.combinationTricast}`;
            case ParticipantPickType.Forecast:
                return this.messages.racingMarketNames.forecast;
            case ParticipantPickType.Tricast:
                return this.messages.racingMarketNames.tricast;
            default:
                return this.messages.racingMarketNames.forecast;
        }
    }

    getTranslatedMarketName(marketType: string, places?: string, optionName?: string): string {
        switch (marketType) {
            case RacingOptionMarketTypes.BettingWithout:
                return this.stringInterpolationPipe.transform(this.messages.racingMarketNames.bettingWithout, {
                    participantName: optionName!,
                });
            case RacingOptionMarketTypes.ToFinishX: {
                if (places === '2') return this.messages.racingMarketNames.toFinish2;
                else if (places === '3') return this.messages.racingMarketNames.toFinish3;
                else return this.messages.racingMarketNames.toFinish4;
            }
            case RacingOptionMarketTypes.TopX: {
                if (places === '2') return this.messages.racingMarketNames.topFinish2;
                else if (places === '3') return this.messages.racingMarketNames.topFinish3;
                else return this.messages.racingMarketNames.topFinish4;
            }
            case RacingOptionMarketTypes.WinEw:
                return this.messages.racingMarketNames.winEw;
            case RacingOptionMarketTypes.WinEwExtraPlace:
                const optionNames = optionName!.split(' ');

                return this.stringInterpolationPipe.transform(this.messages.racingMarketNames.winEwExtraPlace, {
                    terms: optionNames[0],
                    Places: optionNames[1],
                });
            case RacingOptionMarketTypes.Forecast:
                return this.messages.racingMarketNames.forecast;
            case RacingOptionMarketTypes.Tricast:
                return this.messages.racingMarketNames.tricast;
            case RacingOptionMarketTypes.FreeFormed:
                return this.messages.racingMarketNames.specials;
            case RacingOptionMarketTypes.InsuranceX: {
                if (places === '2') return this.messages.racingMarketNames.insurance2Places;
                else if (places === '3') return this.messages.racingMarketNames.insurance3Places;
                else return this.messages.racingMarketNames.insurance4Places;
            }
            case RacingOptionMarketTypes.WinByXlengths: {
                if (places === '1') return this.messages.racingMarketNames.winBy1Lengths;
                else return this.messages.racingMarketNames.winBy3Lengths;
            }
            case RacingOptionMarketTypes.WinnerByHowFar:
                return this.messages.racingMarketNames.winnerByHowFar;

            default:
                return marketType;
        }
    }

    getMarketName(optionMarket: OptionMarket, fixture: OptionMarketsFixture): void {
        const marketType = optionMarket.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value;
        optionMarket.name.value =
            marketType === RacingOptionMarketTypes.BettingWithout
                ? RacingOptionMarketTypes.BettingWithout
                : marketType === RacingOptionMarketTypes.FreeFormed
                  ? RacingOptionMarketTypes.FreeFormed
                  : marketType === RacingOptionMarketTypes.WinnerByHowFar
                    ? RacingOptionMarketTypes.WinnerByHowFar
                    : optionMarket.name.value;

        const participantName =
            optionMarket.fixtureParticipantId &&
            optionMarket.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value === RacingOptionMarketTypes.BettingWithout
                ? fixture.participants?.find((p) => p.id === optionMarket.fixtureParticipantId)?.name.value
                : optionMarket.name.value;

        const optionName =
            marketType === RacingOptionMarketTypes.BettingWithout
                ? participantName
                : marketType === RacingOptionMarketTypes.WinEwExtraPlace
                  ? this.easyBetMarketHelperService.calculateEachWayTerms(optionMarket.placeTerms)
                  : '';

        const isWinBylengths = this.horsesFixtureMarketHelper.checkRacingOptionMarketTypes(
            RacingOptionMarketTypes.WinByXlengths,
            optionMarket.parameters?.find((p) => p.key === Tv2MarketParameters.MarketType)?.value,
        );
        const translatedMarketName = this.getTranslatedMarketName(
            marketType!,
            optionMarket.parameters?.find((param) =>
                isWinBylengths ? param.key === Tv2MarketParameters.Lengths : param.key === Tv2MarketParameters.Places,
            )?.value,
            optionName,
        );
        if (translatedMarketName) optionMarket.name.value = translatedMarketName;
    }

    private mapEntainUIBABMarketOptionAcceptedPrice(
        pick: BetslipBetBuilderPick,
        optionMarketId: number,
        optionId: number,
        optionMarketName: SignedName,
        optionName: SignedName,
        nativeOdds: Odds,
    ): void {
        pick.market = {
            id: optionMarketId,
            isVisible: true,
            isEachWay: false,
            placeTerms: null,
            name: optionMarketName,
            comboPrevention: this.betslipConfig.allowBetbuilderCombos ? CombinationPrevention.NoFixtureCombo : CombinationPrevention.NoCombo,
            minimumCombo: MinimumCombination.Single,
            gridGroups: [],
            isClosed: false,
            isBetBuilderEnabled: false,
        };

        pick.option = {
            id: optionId,
            name: optionName,
            isVisible: true,
            prices: [
                {
                    id: 0,
                    isVisible: true,
                    marketId: optionId,
                    nativeOdds,
                    type: PriceType.Fixed,
                },
            ],
        };

        pick.acceptedPrice = {
            price: pick.currentPrice ? { ...pick.currentPrice } : null,
            isManuallyAccepted: false,
        };
    }
}
